import { AppleLogo } from '@/components/global/brand-icons';
import { Badge } from '@/components/ui/badge';
import {
  type MessageTransport,
  type MessagingPlatform,
  messageTransportLabels,
} from '@/features/conversations/api';
import { cn } from '@/lib/utils';
import {
  Facebook,
  Instagram,
  MessageCircle,
  MessageSquare,
  Phone,
} from 'lucide-react';
import type { ElementType } from 'react';

interface BadgeConfig {
  icon: ElementType;
  label: string;
  className: string;
}

// Keyed by MessagingPlatform, not string: a platform added to the union
// without a badge here is a compile error, not a silent Facebook badge on
// someone's text message.
const platformConfig: Record<MessagingPlatform, BadgeConfig> = {
  facebook_messenger: {
    icon: Facebook,
    label: 'Messenger',
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  },
  instagram_dm: {
    icon: Instagram,
    label: 'Instagram',
    className: 'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300',
  },
  whatsapp: {
    icon: MessageCircle,
    label: 'WhatsApp',
    className:
      'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  },
  sms: {
    icon: MessageSquare,
    label: 'SMS',
    // No brand colour — SMS is the carrier channel, not a third-party
    // platform. Matches ConversationPlatformAvatar's neutral overlay.
    className: 'bg-muted text-muted-foreground',
  },
  phone: {
    icon: Phone,
    label: 'Phone',
    className: 'bg-muted text-muted-foreground',
  },
};

// An SMS conversation names what actually carried it when the provider says
// (LoopMessage picks iMessage, then RCS, then SMS). Plain or unknown stays SMS.
const smsTransportConfig: Partial<Record<MessageTransport, BadgeConfig>> = {
  imessage: {
    icon: AppleLogo,
    label: messageTransportLabels.imessage,
    className: 'bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
  },
  rcs: {
    icon: MessageSquare,
    label: messageTransportLabels.rcs,
    className: 'bg-muted text-muted-foreground',
  },
};

interface PlatformBadgeProps {
  platform: MessagingPlatform;
  transport?: MessageTransport | null;
  className?: string;
}

export function PlatformBadge({
  platform,
  transport,
  className,
}: PlatformBadgeProps) {
  const config =
    (platform === 'sms' && transport && smsTransportConfig[transport]) ||
    platformConfig[platform];
  const Icon = config.icon;

  return (
    <Badge
      variant="secondary"
      className={cn(
        'gap-1 border-0 px-1.5 py-0 text-[10px] font-normal',
        config.className,
        className
      )}
    >
      <Icon className="size-3" />
      {config.label}
    </Badge>
  );
}
